import { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { useAlert } from 'react-alert';
import { Prod01 } from '../../constants/ActionType';

// True only for a Super Admin (role admin with global scope, organizationIds empty).
export const isSuperAdmin = (user) => user?.role === 'admin' && !(user?.organizationIds || []).length;

const errMsg = (error) => error?.response?.data?.message || error.message;

/**
 * Organization(s) multi-select bound to a staff record's organizationIds.
 * kind: 'admin' | 'doctor' | 'hr'. Saves through PUT /organizations/staff/:kind/:id (Super Admin only on the server too).
 * Non-super-admins see a read-only list when `organizationIds` is passed, otherwise nothing.
 * Inactive organizations are never offered as options.
 */
const StaffOrgAssign = ({ kind, staffId, staffRole, organizationIds, onSaved }) => {
  const { user } = useSelector((state) => state.auth);
  const alert = useAlert();
  const [orgs, setOrgs] = useState([]);
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [role, setRole] = useState(staffRole);
  const canUse = isSuperAdmin(user);
  const readOnly = !canUse && Array.isArray(organizationIds);

  useEffect(() => {
    if (!readOnly) return undefined;
    let alive = true;
    axios
      .get(`${Prod01}/general/organizations`)
      .then(({ data }) => alive && setOrgs(data || []))
      .catch(() => alive && setOrgs([]));
    return () => {
      alive = false;
    };
  }, [readOnly]);

  useEffect(() => {
    if (!canUse || !staffId) return undefined;
    let alive = true;
    Promise.all([
      axios.get(`${Prod01}/organizations`),
      axios.get(`${Prod01}/organizations/staff/${kind}/${staffId}`),
    ])
      .then(([o, s]) => {
        if (!alive) return;
        setOrgs(o.data || []);
        setCurrent(s.data.organizationIds || []);
        setSelected(s.data.organizationIds || []);
        if (s.data.role) setRole(s.data.role);
      })
      .catch((e) => alive && alert.error(errMsg(e)));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUse, kind, staffId]);

  const byId = Object.fromEntries(orgs.map((o) => [String(o._id), o]));
  const nameOf = (id) => {
    const o = byId[id];
    if (!o) return 'Other organization';
    return o.status === 'active' ? o.name : `${o.name} (inactive)`;
  };

  if (readOnly) {
    const ids = organizationIds.map(String);
    return (
      <div className="mt-3 p-3" style={{ border: '1px solid #dee2e6', borderRadius: 6 }} data-testid={`org-assign-${kind}`}>
        <label className="form-label">
          <b>Organization(s)</b> <small className="text-muted">(read-only)</small>
        </label>
        <Select isMulti isDisabled value={ids.map((id) => ({ value: id, label: nameOf(id) }))} placeholder="Global (all organizations)" />
        <small className="text-muted">Only a Super Admin can change organization assignments.</small>
      </div>
    );
  }
  if (!canUse) return null;

  const options = orgs.filter((o) => o.status === 'active').map((o) => ({ value: String(o._id), label: o.name }));
  const value = selected.map((id) => options.find((x) => x.value === id) || { value: id, label: nameOf(id) });
  const globalAllowed = kind === 'admin' && role === 'admin';
  const dirty = current !== null && [...selected].sort().join() !== [...current].sort().join();

  const save = async () => {
    if (!selected.length && !globalAllowed) {
      alert.error('Select at least one organization - a user with no organization is locked out.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(`${Prod01}/organizations/staff/${kind}/${staffId}`, { organizationIds: selected });
      setCurrent(data.organizationIds);
      setSelected(data.organizationIds);
      alert.success('Organization scope updated');
      if (onSaved) onSaved(data.organizationIds);
    } catch (e) {
      alert.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 p-3" style={{ border: '1px solid #dee2e6', borderRadius: 6 }} data-testid={`org-assign-${kind}`}>
      <label className="form-label">
        <b>Organization(s)</b> <small className="text-muted">(Super Admin only)</small>
      </label>
      <Select
        isMulti
        options={options}
        value={value}
        placeholder={globalAllowed ? 'None = global (all organizations)' : 'Select organization(s)...'}
        onChange={(vals) => setSelected((vals || []).map((v) => v.value))}
        isDisabled={current === null || saving}
      />
      <small className="text-muted">
        {globalAllowed
          ? 'Leave empty to keep this Super Admin global (organizationIds: []).'
          : 'This account can only see and change data inside the selected organization(s). Takes effect immediately.'}
        {selected.length === 0 && !globalAllowed ? (
          <span style={{ color: 'red' }}> No organization = this user is locked out (403).</span>
        ) : null}
      </small>
      <div className="mt-2">
        <button type="button" className="edit-pill" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving...' : 'Save Organizations'}
        </button>
      </div>
    </div>
  );
};

export default StaffOrgAssign;
